import _extends from "@babel/runtime/helpers/esm/extends";
import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import { MODAL_SIZES } from '../../constants';
import CustomPropTypes from '../../util/custom-prop-types';
import { getSize } from './getSize';
import { StyledModalDialog } from './ModalDialog.styles';

/**
 * A modal dialog component, also commonly know simply as a "modal" or a "lightbox".
 *
 * All props are passed to the underlying `Dialog` component.
 *
 * ##### Known Limitations
 * * In IE11, dialogs always take up the full height of the screen.
 *
 * @extends Modal
 * @related ['Dialog']
 * @since 5.0.0
 */
export var ModalDialog = /*#__PURE__*/forwardRef(function (props, forwardedRef) {
  return /*#__PURE__*/React.createElement(StyledModalDialog, _extends({
    "aria-modal": "true",
    ref: forwardedRef
  }, props, {
    size: getSize(props)
  }));
});
ModalDialog.displayName = 'ModalDialog';
ModalDialog.propTypes = {
  /**
   * Display divider lines between header, body, and footer sections.
   *
   * @since 8.54.0
   */
  dividers: PropTypes.bool,
  /**
   * By default, when there is overflow content in the dialog body, the body will scroll within
   * the dialog bounds. Optionally, you can disable scrolling on the dialog body and instead
   * have the entire dialog scroll within the document bounds.
   */
  scrollDocument: PropTypes.bool,
  /**
   * The size of a modal dialog.
   *
   * Supports inline media query objects
   *
   * @since 8.54.0
   */
  size: CustomPropTypes.inlineMediaQuery(PropTypes.oneOf(Object.values(MODAL_SIZES))),
  /**
   * @deprecated Use the `size="fullScreen"` prop.
   */
  fullScreen: CustomPropTypes.inlineMediaQuery(PropTypes.bool),
  /**
   * @deprecated Use the `size="md"` prop.
   */
  wide: PropTypes.bool,
  /**
   * @deprecated Use inline media query objects
   */
  mediaQuery: CustomPropTypes.mediaQuery
};
ModalDialog.defaultProps = {
  dividers: true,
  fullScreen: false,
  size: MODAL_SIZES.XS,
  wide: false,
  scrollDocument: false
};
ModalDialog.SIZES = MODAL_SIZES;